/**
 * MultiVideoController
 *
 * Manages multiple videos for multi-marker AR experiences.
 * Handles preloading, caching, and seamless switching between marker videos.
 */

import { saveVideo, getVideo } from '../utils/indexeddb.js';
import * as THREE from 'three';

export class VideoLoadError extends Error {
  constructor(videoPath, cause) {
    super(`Failed to load video: ${videoPath}`);
    this.name = 'VideoLoadError';
    this.videoPath = videoPath;
    this.cause = cause;
  }
}

export class VideoNotFoundError extends Error {
  constructor(markerId) {
    super(`Video not found for marker: ${markerId}`);
    this.name = 'VideoNotFoundError';
    this.markerId = markerId;
  }
}

export class VideoNotLoadedError extends Error {
  constructor(markerId) {
    super(`Video not loaded for marker: ${markerId}`);
    this.name = 'VideoNotLoadedError';
    this.markerId = markerId;
  }
}

/**
 * VideoAsset represents a single video resource
 */
class VideoAsset {
  constructor(markerId, videoPath) {
    this.videoId = markerId;
    this.videoPath = videoPath;
    this.videoElement = null;
    this.texture = null;
    this.loadStatus = 'pending'; // pending, loading, loaded, failed
    this.loadProgress = 0;
    this.playbackStatus = 'stopped'; // stopped, playing, paused, buffering
    this.fileSize = null;
    this.isCached = false;
    this.lastError = null;
  }
}

/**
 * MultiVideoController class
 */
export class MultiVideoController {
  constructor() {
    this.videos = new Map(); // markerId -> VideoAsset
    this.currentMarkerId = null;
    this.isPreloading = false;
    this.preloadProgress = 0;
  }

  /**
   * Preload all marker videos
   * @param {Array} markers - Array of MarkerDefinition objects
   * @param {Function} onProgress - Optional progress callback (loaded, total) => void
   * @param {boolean} progressive - Use progressive loading (default: false, disabled)
   * @returns {Promise<void>}
   */
  async preloadAll(markers, onProgress = null, progressive = false) {
    if (this.isPreloading) {
      console.warn('Preload already in progress');
      return;
    }

    this.isPreloading = true;
    const totalMarkers = markers.length;
    let loadedCount = 0;

    console.log(`🎬 Preloading ${totalMarkers} videos (progressive: ${progressive})...`);

    // Create VideoAssets for all markers
    markers.forEach((marker) => {
      const asset = new VideoAsset(marker.id, marker.videoPath);
      this.videos.set(marker.id, asset);
    });

    // T026: Add link preload tags to HTML for browser prefetch
    this._addPreloadLinks(markers);

    if (progressive && markers.length > 1) {
      // Progressive loading: Load first video prioritized, then load rest in parallel
      console.log('📹 Progressive loading: Priority video first...');

      // Load first video (priority)
      const firstMarker = markers[0];
      const firstAsset = this.videos.get(firstMarker.id);

      try {
        await this._loadVideoAsset(firstAsset, (percent) => {
          firstAsset.loadProgress = percent;
          if (onProgress) {
            // Map first video to 0-50% of total progress
            const overallPercent = percent * 0.5;
            onProgress(overallPercent);
          }
        });

        firstAsset.loadStatus = 'loaded';
        loadedCount++;
        console.log(`✅ Priority video loaded: ${firstMarker.id} (1/${totalMarkers})`);
      } catch (error) {
        firstAsset.loadStatus = 'failed';
        firstAsset.lastError = error;
        console.error(`❌ Failed to load priority video ${firstMarker.id}:`, error);
      }

      // Load remaining videos in parallel
      const remainingMarkers = markers.slice(1);
      console.log(`📹 Loading ${remainingMarkers.length} remaining videos in parallel...`);

      const remainingPromises = remainingMarkers.map(async (marker) => {
        const asset = this.videos.get(marker.id);

        try {
          await this._loadVideoAsset(asset, (percent) => {
            asset.loadProgress = percent;

            // Calculate overall progress (50-100% range)
            if (onProgress) {
              let remainingProgress = 0;
              remainingMarkers.forEach((m) => {
                const a = this.videos.get(m.id);
                remainingProgress += a.loadProgress;
              });
              const avgProgress = remainingProgress / remainingMarkers.length;
              const overallPercent = 50 + avgProgress * 0.5;
              onProgress(overallPercent);
            }
          });

          asset.loadStatus = 'loaded';
          loadedCount++;
          console.log(`✅ Video loaded: ${marker.id} (${loadedCount}/${totalMarkers})`);
        } catch (error) {
          asset.loadStatus = 'failed';
          asset.lastError = error;
          console.error(`❌ Failed to load video ${marker.id}:`, error);
        }
      });

      await Promise.all(remainingPromises);
    } else {
      // Standard parallel loading (all at once)
      const preloadPromises = markers.map(async (marker) => {
        const asset = this.videos.get(marker.id);

        try {
          await this._loadVideoAsset(asset, (percent) => {
            asset.loadProgress = percent;

            // Calculate overall progress
            if (onProgress) {
              let totalProgress = 0;
              this.videos.forEach((v) => {
                totalProgress += v.loadProgress;
              });
              const overallPercent = totalProgress / totalMarkers;
              onProgress(overallPercent);
            }
          });

          asset.loadStatus = 'loaded';
          loadedCount++;
          console.log(`✅ Video loaded: ${marker.id} (${loadedCount}/${totalMarkers})`);
        } catch (error) {
          asset.loadStatus = 'failed';
          asset.lastError = error;
          console.error(`❌ Failed to load video ${marker.id}:`, error);
        }
      });

      await Promise.all(preloadPromises);
    }

    this.isPreloading = false;
    this.preloadProgress = 100;

    const successCount = Array.from(this.videos.values()).filter(
      (v) => v.loadStatus === 'loaded'
    ).length;

    console.log(`✅ Preload complete: ${successCount}/${totalMarkers} videos loaded`);

    if (successCount === 0) {
      throw new VideoLoadError('all', new Error('Failed to load any videos'));
    }
  }

  /**
   * Load a single video asset
   * @param {VideoAsset} asset - VideoAsset to load
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<void>}
   * @private
   */
  async _loadVideoAsset(asset, onProgress = null) {
    asset.loadStatus = 'loading';

    try {
      // Check IndexedDB cache first
      const cachedVideo = await getVideo(asset.videoId);

      let blob;
      if (cachedVideo && cachedVideo.blob) {
        console.log(`📦 Video ${asset.videoId} loaded from cache`);
        blob = cachedVideo.blob;
        asset.isCached = true;
        if (onProgress) onProgress(100);
      } else {
        // Download video with progress tracking
        console.log(`⬇️ Downloading video ${asset.videoId}...`);
        const response = await fetch(asset.videoPath);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentLength = response.headers.get('content-length');
        if (!contentLength || !onProgress) {
          blob = await response.blob();
          if (onProgress) onProgress(100);
        } else {
          const total = parseInt(contentLength, 10);
          let loaded = 0;

          const reader = response.body.getReader();
          const chunks = [];

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            chunks.push(value);
            loaded += value.length;

            if (onProgress) {
              const percent = Math.min((loaded / total) * 100, 100);
              onProgress(percent);
            }
          }

          blob = new Blob(chunks);
        }

        asset.fileSize = blob.size;
        console.log(`✅ Downloaded ${asset.videoId}: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);

        // Cache in IndexedDB (async, don't wait)
        saveVideo(asset.videoId, blob, {
          url: asset.videoPath,
          cachedAt: Date.now(),
        }).catch((error) => {
          console.warn(`Failed to cache video ${asset.videoId}:`, error);
        });
      }

      // Create video element
      const videoElement = document.createElement('video');
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('webkit-playsinline', '');
      videoElement.setAttribute('crossorigin', 'anonymous');
      videoElement.setAttribute('preload', 'auto'); // T026: Preload video data
      videoElement.loop = false; // Manual loop control for better control
      videoElement.muted = true; // Required for autoplay

      // Add loop restart handler (conditional - only when actively playing)
      videoElement.addEventListener('ended', () => {
        // Only restart if the video is supposed to be playing
        // This prevents unwanted playback when:
        // - Marker is not visible (playbackStatus = 'stopped' or 'paused')
        // - Browser is in background
        if (asset.playbackStatus === 'playing') {
          console.log(`🔄 Video ended: ${asset.videoId}, restarting from beginning`);
          videoElement.currentTime = 0;
          videoElement.play().catch((error) => {
            console.error(`Failed to restart video ${asset.videoId}:`, error);
          });
        } else {
          console.log(
            `⏸️ Video ended: ${asset.videoId}, not restarting (status: ${asset.playbackStatus})`
          );
        }
      });

      // Create object URL
      const objectURL = URL.createObjectURL(blob);
      videoElement.src = objectURL;

      // Wait for video to be ready
      await new Promise((resolve, reject) => {
        videoElement.onloadeddata = resolve;
        videoElement.onerror = () => reject(new Error('Failed to load video element'));
        // Timeout after 10 seconds
        setTimeout(() => reject(new Error('Video load timeout')), 10000);
      });

      asset.videoElement = videoElement;

      // Create Three.js VideoTexture
      const texture = new THREE.VideoTexture(videoElement);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      // format is auto-detected by Three.js (RGBFormat is deprecated/removed in newer versions)
      asset.texture = texture;

      console.log(`🎬 Video element ready: ${asset.videoId}`);
    } catch (error) {
      throw new VideoLoadError(asset.videoPath, error);
    }
  }

  /**
   * Switch to a different marker's video (synchronous for fast switching)
   * @param {string} markerId - Target marker ID
   * @returns {VideoAsset}
   * @throws {VideoNotFoundError} If marker not found
   * @throws {VideoNotLoadedError} If video not loaded
   */
  switchTo(markerId) {
    const asset = this.videos.get(markerId);

    if (!asset) {
      throw new VideoNotFoundError(markerId);
    }

    if (asset.loadStatus !== 'loaded') {
      throw new VideoNotLoadedError(markerId);
    }

    // Pause current video
    if (this.currentMarkerId && this.currentMarkerId !== markerId) {
      const currentAsset = this.videos.get(this.currentMarkerId);
      if (currentAsset && currentAsset.videoElement) {
        currentAsset.videoElement.pause();
        currentAsset.playbackStatus = 'paused';
      }
    }

    // Play new video (don't await - fire and forget for speed)
    try {
      // Resume from paused position (don't reset currentTime)
      // Video will loop automatically when it ends (handled by 'ended' event)

      // Start playback asynchronously (don't block)
      asset.videoElement.play().catch((error) => {
        console.error(`Failed to play video ${markerId}:`, error);
        asset.playbackStatus = 'stopped';
      });
      asset.playbackStatus = 'playing';

      this.currentMarkerId = markerId;

      console.log(
        `🎥 Switched to video: ${markerId} (resume from ${asset.videoElement.currentTime.toFixed(2)}s)`
      );

      return asset;
    } catch (error) {
      console.error(`Failed to switch video ${markerId}:`, error);
      asset.playbackStatus = 'stopped';
      throw error;
    }
  }

  /**
   * Pause current video
   */
  pause() {
    if (!this.currentMarkerId) return;

    const asset = this.videos.get(this.currentMarkerId);
    if (asset && asset.videoElement) {
      asset.videoElement.pause();
      asset.playbackStatus = 'paused';
    }
  }

  /**
   * Pause all videos and update their playback status
   * Use this when all markers are lost or when going to background
   */
  pauseAll() {
    let pausedCount = 0;
    this.videos.forEach((asset, markerId) => {
      if (asset.videoElement && !asset.videoElement.paused) {
        asset.videoElement.pause();
        pausedCount++;
      }
      // Set to 'stopped' (not 'paused') since this is a complete stop
      asset.playbackStatus = 'stopped';
    });
    if (pausedCount > 0) {
      console.log(`⏸️ All videos paused (${pausedCount} active)`);
    }
  }

  /**
   * Resume current video
   */
  resume() {
    if (!this.currentMarkerId) return;

    const asset = this.videos.get(this.currentMarkerId);
    if (asset && asset.videoElement) {
      asset.videoElement.play().catch((error) => {
        console.error('Failed to resume video:', error);
      });
      asset.playbackStatus = 'playing';
    }
  }

  /**
   * Get current marker ID
   * @returns {string|null}
   */
  getCurrentMarkerId() {
    return this.currentMarkerId;
  }

  /**
   * Get VideoAsset by marker ID
   * @param {string} markerId - Marker ID
   * @returns {VideoAsset|null}
   */
  getVideoAsset(markerId) {
    return this.videos.get(markerId) || null;
  }

  /**
   * Check if video is preloaded
   * @param {string} markerId - Marker ID
   * @returns {boolean}
   */
  isPreloaded(markerId) {
    const asset = this.videos.get(markerId);
    return asset && asset.loadStatus === 'loaded';
  }

  /**
   * Get all loaded video assets
   * @returns {Array<VideoAsset>}
   */
  getAllVideoAssets() {
    return Array.from(this.videos.values());
  }

  /**
   * Get preload statistics
   * @returns {Object}
   */
  getStats() {
    const assets = Array.from(this.videos.values());
    return {
      total: assets.length,
      loaded: assets.filter((a) => a.loadStatus === 'loaded').length,
      failed: assets.filter((a) => a.loadStatus === 'failed').length,
      pending: assets.filter((a) => a.loadStatus === 'pending').length,
      totalSize: assets.reduce((sum, a) => sum + (a.fileSize || 0), 0),
      cached: assets.filter((a) => a.isCached).length,
    };
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.videos.forEach((asset) => {
      if (asset.videoElement) {
        asset.videoElement.pause();
        asset.videoElement.src = '';
        URL.revokeObjectURL(asset.videoElement.src);
      }
      if (asset.texture) {
        asset.texture.dispose();
      }
    });

    this.videos.clear();
    this.currentMarkerId = null;
    this.isPreloading = false;
    this.preloadProgress = 0;

    console.log('MultiVideoController cleaned up');
  }

  /**
   * Mute/unmute all videos
   * @param {boolean} muted - True to mute
   */
  setMuted(muted) {
    console.log(`🔍 [MULTI AUDIO] setMuted called with: ${muted}`);
    console.log(`🔍 [MULTI AUDIO] Total videos: ${this.videos.size}`);

    let updatedCount = 0;
    this.videos.forEach((asset, markerId) => {
      if (asset.videoElement) {
        const beforeMuted = asset.videoElement.muted;
        asset.videoElement.muted = muted;
        console.log(`  ✓ ${markerId}: muted ${beforeMuted} → ${muted}`);
        updatedCount++;
      } else {
        console.warn(`  ⚠️ ${markerId}: No videoElement`);
      }
    });

    console.log(
      `🔊 [MULTI AUDIO] Updated ${updatedCount}/${this.videos.size} videos to muted=${muted}`
    );
  }

  /**
   * T026: Add link preload tags to HTML for browser prefetch
   * @param {Array} markers - Marker configurations
   * @private
   */
  _addPreloadLinks(markers) {
    const head = document.head;

    markers.forEach((marker) => {
      // Check if preload link already exists
      const existingLink = head.querySelector(`link[data-marker-id="${marker.id}"][rel="preload"]`);
      if (existingLink) {
        console.log(`🔗 [T026] Preload link already exists for ${marker.id}`);
        return;
      }

      // Create preload link
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'video';
      link.href = marker.videoPath;
      link.setAttribute('data-marker-id', marker.id);

      // Add cache hint
      link.setAttribute('crossorigin', 'anonymous');

      head.appendChild(link);
      console.log(`🔗 [T026] Added preload link for ${marker.id}: ${marker.videoPath}`);
    });
  }
}
